/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';

// Define the context type
interface HealthContextType {
  systemHealth: boolean | null;
}

// Create the context
const HealthContext = createContext<HealthContextType | undefined>(undefined);

// Custom hook for using the HealthContext
export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within an OrderProvider');
  }
  return context;
};

interface HealthProviderProps {
  children: ReactNode;
}

export const HealthProvider: React.FC<HealthProviderProps> = ({ children }) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('health', reactAppApiUrl).toString();

  const [systemHealth, setSystemHealth] = React.useState<boolean | null>(null);

  // Get system health
  const getSystemHealth = useCallback(async (): Promise<boolean> => {
    try {
      const config = {
        method: 'get',
        url: apiUrl,
      };
      const response = await axios(config);
      return response.data.systemHealth;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  }, [apiUrl]);

  useEffect(() => {
    const fetchHealthStatus = async () => {
      try {
        await getSystemHealth();
        setSystemHealth(true);
      } catch {
        setSystemHealth(false);
      }
    };

    fetchHealthStatus();
  }, [getSystemHealth]);

  return (
    <HealthContext.Provider
      value={{
        systemHealth,
      }}
    >
      {children}
    </HealthContext.Provider>
  );
};
